import React from "react";
import { Show } from "@refinedev/antd";
import { useShow, useResource } from "@refinedev/core";
import { Descriptions, Skeleton, Tag } from "antd";
import type { FieldDef } from "@evo-flywheel/contracts";
import { ShowSlots, useRelatedRecords } from "@evo-flywheel/ui";
import { resourceBySlug } from "../../kit-resources";
import { renderCell } from "../../fields";
import { specs } from "../../specs";

export const GenericShow: React.FC = () => {
  const { resource } = useResource();
  const meta = resourceBySlug[resource?.name ?? ""]?.meta;
  const { queryResult } = useShow();
  const record = queryResult?.data?.data as Record<string, unknown> | undefined;
  const isLoading = queryResult?.isLoading;

  // Only a crud module edits or deletes from its detail page (rule 10 write posture).
  const spec = specs[resource?.name ?? ""];
  const writable = spec?.writePosture === "crud";
  const statusField = spec?.columns.find((c) => c.renderer === "statusTag")?.dataIndex;
  const cell = (name: string) => {
    const v = record?.[name];
    if (name === statusField && v != null && v !== "") {
      const key = String(v);
      return <Tag color={spec?.statusColors?.[key]}>{spec?.statusLabels?.[key] ?? key}</Tag>;
    }
    return renderCell(fieldMap.get(name), v);
  };

  const fieldMap = new Map<string, FieldDef>(
    (meta?.fields ?? []).map((f) => [f.name, f])
  );
  const rows = meta?.showView ?? [];

  // UI contract rule 13: everything linked to the record fills the layout's right column.
  useRelatedRecords(resource?.name, record, String(record?.[rows[0] ?? ""] ?? record?.id ?? ""));

  return (
    <Show isLoading={isLoading} canEdit={writable} canDelete={writable}>
      {isLoading || !record ? (
        <Skeleton active />
      ) : (
        <Descriptions bordered column={1} size="small">
          {rows.map((name) => (
            <Descriptions.Item key={name} label={fieldMap.get(name)?.label ?? name}>
              {cell(name)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}
      {record && <div style={{ marginTop: 16 }}><ShowSlots slug={resource?.name} record={record} /></div>}
    </Show>
  );
};
